import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Heading,
  Textarea,
  Flex,
  FormErrorMessage,
  Text,
} from '@chakra-ui/react';
import FileUpload from '../FileUpload';

// Accept new props: formData, onNext, onBack, onUpdateStepData, onSubmit, isMechanicalSpaceStep
function MechanicalSpaceStep({ formData, onNext, onBack, onUpdateStepData, onSubmit, isMechanicalSpaceStep }) { // Added onUpdateStepData, onSubmit, isMechanicalSpaceStep
  const [stepData, setStepData] = useState({
    mechanicalSpaceName: '',
    mechanicalSpaceDimensions: '',
    mechanicalSpaceTemperature: '',
    mechanicalSpaceVideo: null,
    mechanicalEquipmentPicture: null,
    submissionId: formData && formData.submissionId ? formData.submissionId : undefined,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formData) {
      setStepData(prev => ({
        ...prev,
        ...formData,
        submissionId: formData.submissionId || prev.submissionId || 'unknown'
      }));
    }
  }, [formData]);

  // Update local state and call the update function from parent
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...stepData, [name]: value };
    setStepData(updatedData);
    onUpdateStepData(updatedData); // Use the new update function

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  // Update local state for file upload and call the update function from parent
  const handleFileUpload = (fieldName, files) => {
    const updatedData = { ...stepData, [fieldName]: files };
    setStepData(updatedData);
    onUpdateStepData(updatedData); // Use the new update function

    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!stepData.mechanicalSpaceName) newErrors.mechanicalSpaceName = 'Mechanical Space Room Name is required';
    if (!stepData.mechanicalSpaceDimensions) newErrors.mechanicalSpaceDimensions = 'Mechanical Space Dimensions is required';
    if (!stepData.mechanicalSpaceTemperature) newErrors.mechanicalSpaceTemperature = 'Approximate room temperature is required';
    if (!stepData.mechanicalSpaceVideo || stepData.mechanicalSpaceVideo.length === 0) newErrors.mechanicalSpaceVideo = 'Video of the room is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Submit button click - explicitly call onSubmit from parent
  const handleSubmitClick = () => {
    if (validate()) {
      // Data is already updated via handleChange/handleFileUpload
      onSubmit(); // Call the onSubmit function passed from WizardForm
    }
  };

  return (
    <Box>
      <Heading as="h2" size="lg" mb={6}>
        Potential Mechanical Space
      </Heading>
       <Text fontSize="sm" color="gray.600" mb={6}>
        Please note that we typically use PSA technology to create concentrated oxygen by separating outside air. These machines create about 66dB of noise and approximately 2,000 BTUs of heat per machine. Our team will help you mitigate sound and heat, but it is not possible to mitigate them completely. We typically install in crawl spaces, garages and mechanical rooms.
      </Text>
      <VStack spacing={4}>
        <FormControl id="mechanicalSpaceName" isRequired isInvalid={!!errors.mechanicalSpaceName}>
          <FormLabel>Mechanical Space Room Name</FormLabel>
          <Input
            name="mechanicalSpaceName"
            value={stepData.mechanicalSpaceName || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.mechanicalSpaceName}</FormErrorMessage>
        </FormControl>

        <FormControl id="mechanicalSpaceDimensions" isRequired isInvalid={!!errors.mechanicalSpaceDimensions}>
          <FormLabel>Mechanical Space Dimensions (L x W x H)</FormLabel>
          <Input
            name="mechanicalSpaceDimensions"
            value={stepData.mechanicalSpaceDimensions || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.mechanicalSpaceDimensions}</FormErrorMessage>
        </FormControl>

        <FormControl id="mechanicalSpaceTemperature" isRequired isInvalid={!!errors.mechanicalSpaceTemperature}>
          <FormLabel>Approximate room temperature (°F)</FormLabel>
          <Input
            name="mechanicalSpaceTemperature"
            value={stepData.mechanicalSpaceTemperature || ''}
            onChange={handleChange}
            type="number"
          />
          <FormErrorMessage>{errors.mechanicalSpaceTemperature}</FormErrorMessage>
        </FormControl>

        <FormControl id="mechanicalSpaceVideo" isRequired isInvalid={!!errors.mechanicalSpaceVideo}>
          <FormLabel>Please upload a video of the room</FormLabel>
          <FileUpload
            fieldName="mechanicalSpaceVideo"
            onFilesUpload={(keys) => handleFileUpload('mechanicalSpaceVideo', keys)}
            allowMultiple={true}
            accept="video/*" // Pass accept prop
            submissionId={formData.submissionId}
          />
           <FormErrorMessage>{errors.mechanicalSpaceVideo}</FormErrorMessage>
        </FormControl>

        <FormControl id="mechanicalEquipmentPicture" isInvalid={!!errors.mechanicalEquipmentPicture}>
          <FormLabel>If there is any other mechanical equipment, please take a picture of the manufacturer and model number.</FormLabel>
          <FileUpload
            fieldName="mechanicalEquipmentPicture"
            onFilesUpload={(keys) => handleFileUpload('mechanicalEquipmentPicture', keys)}
            allowMultiple={true}
            accept="image/*" // Pass accept prop
            submissionId={formData.submissionId}
          />
           <FormErrorMessage>{errors.mechanicalEquipmentPicture}</FormErrorMessage>
        </FormControl>

        {/* Navigation buttons for Mechanical Space Step */}
        <Flex width="100%" justify="space-between">
           <Button onClick={onBack} variant="outline">
            Back
          </Button>
          {/* This button now explicitly calls onSubmit */}
          <Button colorScheme="green" onClick={handleSubmitClick}>
            Submit
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}

export default MechanicalSpaceStep;
